import type { BrandingSettings, BrandingUiOptions } from "@/api/admin/branding"
import type { LanguageCode } from "@/lib/languages"
import type { InjectedColorMode, UiOptionKey } from "@/lib/runtime-branding"

import { SUPPORTED_LANGUAGES } from "@/lib/languages"

/**
 * The branding fields edited in the Interface section: settings that used to
 * need a customer branch without being styling (#3224). Kept as one value so
 * the panel hydrates, dirty-checks and saves them together.
 */
export type BrandingInterfaceValue = Pick<
  BrandingSettings,
  "ui_options" | "enabled_languages" | "privacy_policy_url" | "default_color_mode"
>

export const EMPTY_INTERFACE: BrandingInterfaceValue = {
  ui_options: {},
  enabled_languages: null,
  privacy_policy_url: null,
  default_color_mode: null,
}

export function interfaceFromSettings(settings: BrandingSettings): BrandingInterfaceValue {
  return {
    ui_options: { ...settings.ui_options },
    enabled_languages: settings.enabled_languages ? [...settings.enabled_languages] : null,
    privacy_policy_url: settings.privacy_policy_url,
    // `?? null`: a document saved before the field existed has no key at all.
    default_color_mode: settings.default_color_mode ?? null,
  }
}

/** Key order does not matter; `{a, b}` and `{b, a}` are the same settings. */
function sameUiOptions(a: BrandingUiOptions, b: BrandingUiOptions): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)] as UiOptionKey[])
  return [...keys].every((key) => a[key] === b[key])
}

export function sameInterface(a: BrandingInterfaceValue, b: BrandingInterfaceValue): boolean {
  return (
    sameUiOptions(a.ui_options, b.ui_options) &&
    JSON.stringify(a.enabled_languages) === JSON.stringify(b.enabled_languages) &&
    a.privacy_policy_url === b.privacy_policy_url &&
    a.default_color_mode === b.default_color_mode
  )
}

/** The languages a stored value offers: the list itself, or every language for null. */
export function offeredLanguages(enabled: LanguageCode[] | null): LanguageCode[] {
  return enabled ?? SUPPORTED_LANGUAGES.map((language) => language.code)
}

/**
 * Tick or untick one language. The result is kept in switcher order, the last
 * language cannot be removed (the page needs one), and ticking every language
 * stores null so a language added to the app later reaches this deployment.
 */
export function toggleLanguage(
  enabled: LanguageCode[] | null,
  code: LanguageCode,
  checked: boolean
): LanguageCode[] | null {
  const current = new Set(offeredLanguages(enabled))
  if (checked) {
    current.add(code)
  } else if (current.size > 1) {
    current.delete(code)
  }
  const next = SUPPORTED_LANGUAGES.map((language) => language.code).filter((c) => current.has(c))
  return next.length === SUPPORTED_LANGUAGES.length ? null : next
}

export type UiOptionChoice = "build" | "on" | "off"

export function choiceFor(value: boolean | undefined): UiOptionChoice {
  if (value === undefined) {
    return "build"
  }
  return value ? "on" : "off"
}

/** "build" removes the key (nothing stored); on/off store an explicit override. */
export function applyUiOptionChoice(
  options: BrandingUiOptions,
  key: UiOptionKey,
  choice: UiOptionChoice
): BrandingUiOptions {
  const next: BrandingUiOptions = { ...options }
  if (choice === "build") {
    delete next[key]
  } else {
    next[key] = choice === "on"
  }
  return next
}

/** The select's value for "nothing stored": Radix Select items cannot use "". */
export type ColorModeChoice = InjectedColorMode | "unset"

export const COLOR_MODE_CHOICES: readonly ColorModeChoice[] = ["unset", "light", "dark", "system"]

export function colorModeChoiceFor(value: InjectedColorMode | null): ColorModeChoice {
  return value ?? "unset"
}

/** "unset" stores null (light, as before the setting existed); the rest are stored as-is. */
export function colorModeFromChoice(choice: ColorModeChoice): InjectedColorMode | null {
  return choice === "unset" ? null : choice
}
